import React, { useMemo, useState } from 'react';
import { Alert, Box, Card, Grid } from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import DataTable from '../../../sharedModules/dataTable';
import { IDataTableColumn } from '../../../sharedModules/dataTable/DataTable/model';
import PageHeader from '../../../sharedModules/layout/PageHeader';
import { useCollectionsLocation } from '../../shared/useCollectionsLocation';
import { useCollectionEmailSends } from '../hooks/useCollectionEmailSends';
import { CollectionEmailSendListItem, CollectionEmailType } from '../model';
import EmailHistoryFilters, { FilterFields } from '../components/EmailHistoryFilters';
import EmailHistoryDetailModal from '../components/EmailHistoryDetailModal';
import { ModuleChip, SourceChip, StatusChip, ToneChip } from '../components/EmailHistoryChips';

// Selectors are real column names so onSort can hand them straight to the API. Every column sorts
// server-side. Module-level so the array identity is stable — DataTable regenerates every column
// id when this prop changes.
const COLUMNS = [
  { name: 'Company', selector: 'company', grow: 2, minWidth: '280px' },
  { name: 'Bill Code', selector: 'bill_code', width: '120px' },
  {
    name: 'Module',
    selector: 'email_type',
    cell: (row: CollectionEmailSendListItem) => <ModuleChip emailType={row.email_type} />,
    width: '120px',
  },
  {
    name: 'Tone',
    selector: 'tone',
    cell: (row: CollectionEmailSendListItem) => <ToneChip row={row} />,
    width: '160px',
  },
  {
    name: 'Status',
    selector: 'status',
    cell: (row: CollectionEmailSendListItem) => <StatusChip status={row.status} />,
    width: '110px',
  },
  {
    name: 'Source',
    selector: 'send_source',
    cell: (row: CollectionEmailSendListItem) => <SourceChip row={row} />,
    width: '120px',
  },
  {
    name: 'Sent At',
    selector: 'delivered_at',
    format: (row: CollectionEmailSendListItem) => row.delivered_at ?? '—',
    width: '180px',
  },
].map((column) => ({ sortable: true, ...column }));

const PER_PAGE_DEFAULT = 25;
// Matches the API's own default order, so the header arrow and the server agree before any click.
const DEFAULT_SORT_FIELD = 'delivered_at';
const DEFAULT_SORT = `${DEFAULT_SORT_FIELD} desc`;

const EmailHistoryPage: React.FC = () => {
  const location = useCollectionsLocation();
  const [filters, setFilters] = useState<FilterFields>({});
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(PER_PAGE_DEFAULT);
  const [sort, setSort] = useState(DEFAULT_SORT);
  const [selectedRow, setSelectedRow] = useState<{ id: number; emailType: CollectionEmailType } | null>(null);

  const cleanedFilters = useMemo(() => {
    return Object.fromEntries(
      Object.entries(filters).filter(([, v]) => v !== '' && v != null),
    ) as Partial<FilterFields>;
  }, [filters]);

  const { data, isLoading, isError } = useCollectionEmailSends({
    location: location ?? '',
    page,
    per_page: perPage,
    sort,
    ...cleanedFilters,
  });

  const rows = data?.data?.result ?? [];
  const totalRecords = data?.data?.total_records ?? 0;

  const handleFiltersChange = (newFilters: FilterFields) => {
    setFilters(newFilters);
    setPage(1);
  };

  const handlePageChange = (nextPage: number) => {
    setPage(nextPage);
  };

  const handleRowsPerPageChange = (nextPerPage: number) => {
    setPerPage(nextPerPage);
    setPage(1);
  };

  const handleSort = (column: IDataTableColumn<CollectionEmailSendListItem>, direction: 'asc' | 'desc') => {
    setSort(`${column.selector} ${direction}`);
    setPage(1);
  };

  if (!location) {
    return (
      <Grid container>
        <Grid item xs={12}>
          <PageHeader mainTitle="Email History" titleIcon={<HistoryIcon />} />
        </Grid>
        <Grid item xs={12} sx={{ mt: 2 }}>
          <Alert severity="info">No Collections location is assigned to this account.</Alert>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container>
      <Grid item xs={12}>
        <PageHeader mainTitle="Email History" titleIcon={<HistoryIcon />} />
      </Grid>
      <Grid item xs={12} sx={{ mt: 2 }}>
        <EmailHistoryFilters onChange={handleFiltersChange} />
      </Grid>
      <Grid item xs={12} sx={{ mt: 2.5 }}>
        {isError && <Box sx={{ p: 4, textAlign: 'center', color: 'error.main' }}>Failed to load email history.</Box>}
        <Card>
          <DataTable
            keyField="id"
            columns={COLUMNS}
            data={rows}
            progressPending={isLoading}
            pagination
            paginationServer
            paginationPerPage={perPage}
            paginationRowsPerPageOptions={[10, 25, 50, 100]}
            paginationTotalRows={totalRecords}
            onChangePage={handlePageChange}
            onChangeRowsPerPage={handleRowsPerPageChange}
            sortServer
            defaultSortField={DEFAULT_SORT_FIELD}
            defaultSortAsc={false}
            onSort={handleSort}
            highlightOnHover
            striped
            noHeader
            pointerOnHover
            onRowClicked={(row: CollectionEmailSendListItem) =>
              setSelectedRow({ id: row.id, emailType: row.email_type })
            }
            noDataComponent={<Box sx={{ p: 3, color: 'text.secondary' }}>No email history found.</Box>}
          />
        </Card>
      </Grid>

      <EmailHistoryDetailModal
        open={selectedRow !== null}
        emailSendId={selectedRow?.id ?? null}
        onClose={() => setSelectedRow(null)}
        emailType={selectedRow?.emailType}
        location={location}
      />
    </Grid>
  );
};

export default EmailHistoryPage;
